import { MONEY_PRIVACY_CLASS } from "../../../utils/moneyPrivacy";
import { useTranslation } from "react-i18next";
import type { CostAmounts, ExpectedCostGroup, ExpectedToDate } from "@torre/work-hours";
import { money } from "./ExpectedKpis";

// ---------------------------------------------------------------------------
// Esperado vs real EN DINERO. Solo dueños: la API manda `expected.cost` nada más
// a quien administra (lleva sueldos), y sin ese campo esta tarjeta no existe.
// Son las mismas horas de «Esperado a hoy vs real» × el costo por hora de cada
// colaborador, con el total y el desglose por squad (con «Sin squad») y por
// proyecto. Los importes van por moneda y nunca se suman entre sí: no hay tipo
// de cambio.
//
// #1281: las horas por aprobar valen `pendingCost` y NO están en «Real». Debajo
// de «Real» va «Por aprobar: $X» y, en «Diferencia», la segunda línea
// «+ Horas por aprobar» = real + por aprobar − esperado, en dinero.
// ---------------------------------------------------------------------------

type Field = keyof CostAmounts | "gapCost" | "gapWithPendingCost";

/** Importe de un campo; `gapCost` = real − esperado; `gapWithPendingCost` = real + por aprobar − esperado. */
const amountOf = (a: CostAmounts, field: Field) =>
  field === "gapCost" ? a.actualCost - a.expectedCost : field === "gapWithPendingCost" ? a.actualCost + (a.pendingCost ?? 0) - a.expectedCost : (a[field] ?? 0);

/** ¿El backend ya manda el costo por aprobar (#1281)? */
const hasPendingCost = (byCurrency: Record<string, CostAmounts>): boolean => Object.values(byCurrency).some((a) => typeof a.pendingCost === "number");

/** Un importe por moneda, separados: no hay tipo de cambio para sumarlos. */
const amounts = (byCurrency: Record<string, CostAmounts>, field: Field, language: string) =>
  Object.keys(byCurrency)
    .sort()
    .map((c) => money(amountOf(byCurrency[c], field), c, language))
    .join(" · ") || "—";

/**
 * Importes de una celda: una moneda por renglón, sin partirse a media cifra, con
 * dígitos de ancho fijo para que las columnas queden alineadas a la derecha.
 * Lo negativo va en rojo (un −0 por redondeo no cuenta como negativo).
 */
function Amounts({ byCurrency, field, language, className = "" }: { byCurrency: Record<string, CostAmounts>; field: Field; language: string; className?: string }) {
  const codes = Object.keys(byCurrency).sort();
  if (!codes.length) return <span>—</span>;
  return (
    <>
      {codes.map((c) => {
        const value = Math.round(amountOf(byCurrency[c], field)) || 0;
        return (
          <span key={c} className={`block whitespace-nowrap tabular-nums ${value < 0 ? "text-red-500" : className}`} data-negative={value < 0 || undefined}>
            {money(value, c, language)}
          </span>
        );
      })}
    </>
  );
}

interface Props {
  expected: ExpectedToDate;
  cardClass: string;
  textClass: string;
  secondaryClass: string;
}

export default function ExpectedCostCard({ expected, cardClass, textClass, secondaryClass }: Props) {
  const { t, i18n } = useTranslation("common");
  const cost = expected.cost;
  if (!cost) return null;
  const language = i18n?.language || "es";
  const priced = Object.keys(cost.byCurrency).length > 0;
  const pending = hasPendingCost(cost.byCurrency);
  // El orden de los proyectos es el de la tabla de horas, para leerlas lado a lado.
  const order = new Map((expected.breakdown?.byProject ?? []).map((p, i) => [p.projectId, i]));
  const projects = [...cost.byProject].sort((a, b) => (order.get(a.projectId) ?? 1e9) - (order.get(b.projectId) ?? 1e9));
  const total: ExpectedCostGroup = { byCurrency: cost.byCurrency, unpricedPeople: cost.unpricedMembers.length };
  const kpis: { key: Field; label: string; hint: string }[] = [
    { key: "expectedCost", label: t("workHours.expected.expected"), hint: t("workHours.expected.cost.rateHint") },
    { key: "actualCost", label: t("workHours.expected.actual"), hint: t("workHours.expected.cost.rateHint") },
    { key: "gapCost", label: t("workHours.expected.gap"), hint: t("workHours.expected.cost.gapHint") },
  ];
  return (
    <div data-testid="expected-cost" className={MONEY_PRIVACY_CLASS}>
      <p className={`mb-2 flex items-center gap-1 text-sm font-semibold ${textClass}`}>
        {t("workHours.expected.cost.title")}
        <span className="rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] font-medium text-violet-500">{t("workHours.expected.cost.ownersOnly")}</span>
        <i className="ri-information-line cursor-help text-xs opacity-70" title={t("workHours.expected.cost.rateHint")} aria-label={t("workHours.expected.cost.rateHint")} />
      </p>
      {priced && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {kpis.map((k) => (
            <div key={k.key} className={cardClass} data-testid={`expected-cost-kpi-${k.key}`} title={k.hint}>
              <p className={`text-xs ${secondaryClass}`}>{k.label}</p>
              <div className={`mt-1 text-xl font-bold ${textClass}`}>
                <Amounts byCurrency={cost.byCurrency} field={k.key} language={language} />
              </div>
              {k.key === "actualCost" && pending && (
                <p className="mt-0.5 text-[11px] text-amber-600" title={t("workHours.expected.pendingHint")} data-testid="expected-cost-pending">
                  <i className="ri-hourglass-line mr-1" />
                  {t("workHours.expected.cost.pending", { amount: amounts(cost.byCurrency, "pendingCost", language) })}
                </p>
              )}
              {k.key === "gapCost" && (
                <>
                  <p className="mt-0.5 text-[11px] text-red-500" title={t("workHours.expected.cost.lossHint")} data-testid="expected-cost-loss">
                    {t("workHours.expected.cost.loss", { hours: cost.shortfallHours, amount: amounts(cost.byCurrency, "lossCost", language) })}
                  </p>
                  {pending && (
                    <p className={`mt-0.5 text-[11px] ${secondaryClass}`} title={t("workHours.expected.cost.gapWithPendingHint")} data-testid="expected-cost-gap-with-pending">
                      {t("workHours.expected.gapWithPending")}:{" "}
                      <span className={`font-medium ${textClass}`}>{amounts(cost.byCurrency, "gapWithPendingCost", language)}</span>
                    </p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {priced && (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <CostTable
            title={t("workHours.expected.bySquad")}
            head={t("workHours.expected.squad")}
            testId="expected-cost-by-squad"
            rows={cost.bySquad.map((s) => ({ key: s.squadId ?? "none", label: s.squadName ?? t("workHours.expected.noSquad"), muted: s.squadId === null, line: s }))}
            total={total}
            pending={pending}
            language={language}
            cardClass={cardClass}
            textClass={textClass}
            secondaryClass={secondaryClass}
          />
          <CostTable
            title={t("workHours.expected.byProject")}
            head={t("workHours.expected.project")}
            testId="expected-cost-by-project"
            rows={projects.map((p) => ({ key: p.projectId, label: p.projectName, muted: false, line: p }))}
            total={total}
            pending={pending}
            language={language}
            cardClass={cardClass}
            textClass={textClass}
            secondaryClass={secondaryClass}
          />
        </div>
      )}
      {cost.unpricedMembers.length > 0 && (
        <p className={`mt-2 text-[11px] ${secondaryClass}`} data-testid="expected-cost-unpriced">
          <i className="ri-money-dollar-circle-line mr-1" />
          {t("workHours.expected.cost.unpriced", { names: cost.unpricedMembers.map((m) => m.name).join(", ") })}
        </p>
      )}
    </div>
  );
}

interface CostTableProps {
  title: string;
  head: string;
  testId: string;
  rows: { key: string; label: string; muted: boolean; line: ExpectedCostGroup }[];
  total: ExpectedCostGroup;
  /** #1281: pintar la columna «Por aprobar». */
  pending: boolean;
  language: string;
  cardClass: string;
  textClass: string;
  secondaryClass: string;
}

/** Esperado · real · (por aprobar) · diferencia en dinero por fila (squad o proyecto), con el total al pie. */
function CostTable({ title, head, testId, rows, total, pending, language, cardClass, textClass, secondaryClass }: CostTableProps) {
  const { t } = useTranslation("common");
  const fields: Field[] = pending ? ["expectedCost", "actualCost", "pendingCost", "gapCost"] : ["expectedCost", "actualCost", "gapCost"];
  const labels: Record<string, string> = {
    expectedCost: t("workHours.expected.expected"),
    actualCost: t("workHours.expected.actual"),
    pendingCost: t("workHours.expected.pending"),
    gapCost: t("workHours.expected.gap"),
  };
  const cells = (line: ExpectedCostGroup) =>
    fields.map((field) => (
      <td key={field} className="py-1.5 pl-4 text-right align-top">
        <Amounts byCurrency={line.byCurrency} field={field} language={language} className={field === "pendingCost" ? "text-amber-600" : ""} />
      </td>
    ));
  const width = pending ? "w-[19%]" : "w-[23%]";
  return (
    <div className={cardClass} data-testid={testId}>
      <p className={`mb-2 text-sm font-semibold ${textClass}`}>{title}</p>
      <div className="overflow-x-auto">
        {/* Columnas de importes del mismo ancho: las dos tablas se leen parejo. */}
        <table className={`w-full ${pending ? "min-w-[36rem]" : "min-w-[30rem]"} table-fixed text-sm`}>
          <colgroup>
            <col />
            {fields.map((f) => (
              <col key={f} className={width} />
            ))}
          </colgroup>
          <thead>
            <tr className={`text-left text-xs ${secondaryClass}`}>
              <th className="py-1 font-medium">{head}</th>
              {fields.map((f) => (
                <th key={f} className="whitespace-nowrap py-1 pl-4 text-right font-medium" title={f === "pendingCost" ? t("workHours.expected.pendingHint") : undefined}>
                  {labels[f]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-t border-gray-500/20 ${r.muted ? secondaryClass : textClass}`} data-testid={`${testId}-${r.key}`}>
                <td className="py-1.5 align-top">
                  {r.label}
                  {r.line.unpricedPeople > 0 && (
                    <span className={`block text-[11px] ${secondaryClass}`}>{t("workHours.expected.cost.unpricedPeople", { count: r.line.unpricedPeople })}</span>
                  )}
                </td>
                {cells(r.line)}
              </tr>
            ))}
            <tr className={`border-t border-gray-500/40 font-semibold ${textClass}`} data-testid={`${testId}-total`}>
              <td className="py-1.5 align-top">{t("workHours.expected.total")}</td>
              {cells(total)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
